import { cs as dateFnsCsLocale } from "date-fns/locale/cs";
import { de as dateFnsDeLocale } from "date-fns/locale/de";
import { enUS as dateFnsEnLocale } from "date-fns/locale/en-US";
import i18n from "i18next";
import { cs, de, en } from "zod/locales";

/**
 * Returns the BCP-47 locale string based on current i18n language.
 */
export function getCurrentLocale(): string {
  switch (i18n.language) {
    case "cs":
      return "cs-CZ";
    case "de":
      return "de-DE";
    default:
      return "en-US";
  }
}

/**
 * Returns the Zod locale config based on current i18n language.
 */
export function getZodLocale() {
  switch (i18n.language) {
    case "cs":
      return cs();
    case "de":
      return de();
    default:
      return en();
  }
}

/**
 * Returns the date-fns Locale object for the current i18n language.
 */
export function getDateFnsLocale() {
  switch (i18n.language) {
    case "cs":
      return dateFnsCsLocale;
    case "de":
      return dateFnsDeLocale;
    default:
      return dateFnsEnLocale;
  }
}

/**
 * Returns the date format pattern for the current language.
 */
export function getDateFormat(): string {
  return i18n.language === "en" ? "MM/dd/yyyy" : "dd.MM.yyyy";
}

/**
 * Returns the date-time format pattern for the current language.
 */
export function getDateTimeFormat(): string {
  return i18n.language === "en" ? "MM/dd/yyyy HH:mm" : "dd.MM.yyyy HH:mm";
}

/**
 * Formats a number using the current i18n locale.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getCurrentLocale(), options).format(value);
}

/**
 * Formats a number as currency using the current i18n locale.
 */
export function formatCurrency(value: number, currency = "EUR"): string {
  return new Intl.NumberFormat(getCurrentLocale(), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formats a number with fixed decimal places using the current locale.
 */
export function localeValue(
  value: number | null | undefined,
  fallback = "-",
  fractionDigits = 2,
): string {
  if (value === null || value === undefined) return fallback;
  return new Intl.NumberFormat(getCurrentLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}
